import React, { useState, useEffect } from "react";
import { Button, CardContent } from "@material-ui/core";
import { useFormik } from "formik";
import { SelectInput, TextInput, CheckBoxInput, DateInput, TextAreaInput } from "../../../../components/Forms";
import {
  FormGroup,
  ButtonWrapperFilter,
  useStyles
} from "../../../../styles/common-styles";
import { useTranslation } from "react-i18next";
import { financialAid_userDefinedFields } from "../../../../services/navigation_folder/financialAid";
import PopupHeader from "../../../common-page/popupHeader";
import * as yup from 'yup';
import { createYupSchema, parseNumber, scrollIntoViewHelper } from "../../../../helper";
import currencyPipe, { formNumber } from "../../../../helper/currency";
import { get_userdefined_data, userdefined_update, user_defined_setup_table } from "../../../../services/apiURL";
import { getApi, putApi } from "../../../../services/apiBase";
import { toastMessage } from "../../../../utils/Message/toasts";
import { maintenance } from "../../../../helper/constants";
import { updateAccessSelector } from "../../../../redux/common/roleAccess/selectors";
import { useSelector } from "react-redux";

const getFieldKeyFromPhysicalName = (physicalName) => {
  const mapping = {
    UserDefGlossary1: "brandonTest1",
    UserDefGlossary2: "brandonTest2", 
    UserDefGlossary3: "brandonTest3",
    UserDefFreeFormat1: "brandonTest",
    UserDefFreeFormat2: "tianaTest1",
    UserDefFreeFormat3: "userDefFreeFormat3",
    UserDefDate1: "finAidDate",
    UserDefDate2: "userDefDate2",
    UserDefDate3: "userDefDate3",
    UserDefReal1: "userDefReal1",
    UserDefReal2: "userDefReal2",
    UserDefReal3: "userDefReal3",
    UserDefMoney1: "userDefinedMoney1",
    UserDefMoney2: "userDefinedMoney2",
    UserDefMoney3: "userDefinedMoney3",
    UserDefBit1: "thirdPartyPackage",
    UserDefBit2: "userDefBit2",
    UserDefBit3: "userDefBit3",
    UserDefNote1: "userDefNote1",
    UserDefNote2: "userDefNote2",
    UserDefNote3: "userDefNote3",
  };
  return mapping[physicalName];
};

const getFieldValidationConfig = (physicalName, displayName, t) => {
  const configs = {
    UserDefGlossary1: {
      validationType: "number",
      validations: [
        { type: "min", params: [1, `${displayName} is required`] },
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "select",
      dropdownKey: "brandonTest1Drop"
    },
    UserDefGlossary2: {
      validationType: "number",
      validations: [
        { type: "min", params: [1, `${displayName} is required`] },
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "select",
      dropdownKey: "brandonTest2Drop"
    },
    UserDefGlossary3: {
      validationType: "number",
      validations: [
        { type: "min", params: [1, `${displayName} is required`] },
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "select",
      dropdownKey: "brandonTest3Drop"
    },
    UserDefFreeFormat1: {
      validationType: "string",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "text"
    },
    UserDefFreeFormat2: {
      validationType: "string",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "text"
    },
    UserDefFreeFormat3: {
      validationType: "string",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "text"
    },
    UserDefDate1: {
      validationType: "date",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
        {
          type: "max",
          params: [
            new Date(t("validationMessages.maxDate")),
            `${displayName} is required`,
          ],
        },
        { type: "typeError", params: [`${displayName} is required`] },
      ],
      field: "date",
    },
    UserDefDate2: {
      validationType: "date",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
        {
          type: "max",
          params: [
            new Date(t("validationMessages.maxDate")),
            `${displayName} is required`,
          ],
        },
        { type: "typeError", params: [`${displayName} is required`] },
      ],
      field: "date",
    },
    UserDefDate3: {
      validationType: "date",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
        {
          type: "max",
          params: [
            new Date(t("validationMessages.maxDate")),
            `${displayName} is required`,
          ],
        },
        { type: "typeError", params: [`${displayName} is required`] },
      ],
      field: "date",
    },
    UserDefReal1: {
      validationType: "number",
      validations: [
        { type: "min", params: [0, `${displayName} must be 0 or greater`] },
        { type: "required", params: [`${displayName} is required`] },
        { type: "typeError", params: [`${displayName} is required`] },
      ],
      field: "text",
      isNumeric: true
    },
    UserDefReal2: {
      validationType: "number",
      validations: [
        { type: "min", params: [0, `${displayName} must be 0 or greater`] },
        { type: "required", params: [`${displayName} is required`] },
        { type: "typeError", params: [`${displayName} is required`] },
      ],
      field: "text",
      isNumeric: true
    },
    UserDefReal3: {
      validationType: "number",
      validations: [
        { type: "min", params: [0, `${displayName} must be 0 or greater`] },
        { type: "required", params: [`${displayName} is required`] },
        { type: "typeError", params: [`${displayName} is required`] },
      ],
      field: "text",
      isNumeric: true
    },
    UserDefMoney1: {
      validationType: "currency",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
        { type: "min", params: [0, `${displayName} must be 0 or greater`] },
      ],
      field: "text",
      isCurrency: true
    },
    UserDefMoney2: {
      validationType: "currency",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
        { type: "min", params: [0, `${displayName} must be 0 or greater`] },
      ],
      field: "text",
      isCurrency: true
    },
    UserDefMoney3: {
      validationType: "currency",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
        { type: "min", params: [0, `${displayName} must be 0 or greater`] },
      ],
      field: "text",
      isCurrency: true
    },
    UserDefBit1: {
      validationType: "bool",
      validations: [
        { type: "oneOf", params: [[true], `${displayName} is required`] },
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "checkbox",
    },
    UserDefBit2: {
      validationType: "bool",
      validations: [
        { type: "oneOf", params: [[true], `${displayName} is required`] },
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "checkbox",
    },
    UserDefBit3: {
      validationType: "bool",
      validations: [
        { type: "oneOf", params: [[true], `${displayName} is required`] },
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "checkbox",
    },
    UserDefNote1: {
      validationType: "string",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "textarea"
    },
    UserDefNote2: {
      validationType: "string",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "textarea"
    },
    UserDefNote3: {
      validationType: "string",
      validations: [
        { type: "required", params: [`${displayName} is required`] },
      ],
      field: "textarea"
    },
  };
  return configs[physicalName] || {};
};

const AddUserdefinedfields = ({ onClose, Dropdown, selectedSelected }) => {
  const { t } = useTranslation(['translation', 'financialAid', 'financialAidHeadcells']);
  const classess = useStyles();

  const initCurrencyFields = (value) => {
    // If value is explicitly 0 or '0', return formatted zero
    if (value === 0 || value === '0') {
      return transform(0);
    }
    // If value is null, undefined, or empty string, return empty string
    if (value === null || value === undefined || value === '') {
      return '';
    }
    // Format the number value
    return transform(parseNumber(value));
  };

  const onSwipeCurrency = (e) => {
    const { name, value } = e.target;
    if (value === "") {
      formik.setFieldValue(name, "");
      return;
    }
    const amount = transform(parseNumber(value));
    formik.setFieldValue(name, amount);
  };

  const transform = (val) => {
    return currencyPipe(val, { symbol: '$', decimal: '.', separator: ',', precision: 2 }).format();
  };

  const [fieldName, setFieldName] = useState({});
  const [schema, setSchema] = useState({});

  const initialValues = {
    brandonTest1: 0,
    brandonTest2: 0,
    brandonTest3: 0,
    finAidDate: null,
    userDefReal1: '',
    userDefinedMoney1: '',
    thirdPartyPackage: false,
    userDefNote1: "",
    userDefBit2: false,
    userDefNote2: "",
    userDefNote3: "",
    userDefDate3: null,
    userDefDate2: null,
    userDefBit3: false,
    brandonTest: '',
    tianaTest1: '',
    userDefFreeFormat3: '',
    userDefReal2: '',
    userDefReal3: '',
    userDefinedMoney2: '',
    userDefinedMoney3: ''
  };

  const [formValues, setFormValues] = useState(initialValues);
  const [userDefinedData, setUserDefinedData] = useState({});

  useEffect(() => {
    if (!selectedSelected?.studentUID) return;

    const loadData = async () => {
      try {
        const userDataResp = await getApi(`${get_userdefined_data}/${selectedSelected.studentUID}`);
        const userData = userDataResp.data.data;
        
        if (userData) {
          const processedUserData = {
            ...initialValues, // Start with default values
            ...userData,     // Override with API data
            // Map backend field names to frontend field names
            userDefReal1: userData.userDefReal1 != null ? userData.userDefReal1.toString() : '',
            userDefReal2: userData.userDefReal2 != null ? userData.userDefReal2.toString() : '',
            userDefReal3: userData.userDefReal3 != null ? userData.userDefReal3.toString() : '',
            // Map money fields from backend (userDefMoney*) to frontend (userDefinedMoney*)
            userDefinedMoney1: userData.userDefMoney1 != null ? initCurrencyFields(userData.userDefMoney1) : 
                             (userData.userDefinedMoney1 != null ? initCurrencyFields(userData.userDefinedMoney1) : ""),
            userDefinedMoney2: userData.userDefMoney2 != null ? initCurrencyFields(userData.userDefMoney2) : 
                             (userData.userDefinedMoney2 != null ? initCurrencyFields(userData.userDefinedMoney2) : ""),
            userDefinedMoney3: userData.userDefMoney3 != null ? initCurrencyFields(userData.userDefMoney3) : 
                             (userData.userDefinedMoney3 != null ? initCurrencyFields(userData.userDefinedMoney3) : ""),
            // Ensure boolean fields are properly set
            thirdPartyPackage: !!userData.thirdPartyPackage,
            userDefBit2: !!userData.userDefBit2,
            userDefBit3: !!userData.userDefBit3
          };

          setFormValues(processedUserData);
          setUserDefinedData(userData);
        }

        const configResp = await getApi(`${user_defined_setup_table}/6`);
        const { data } = configResp.data;

        const fieldMapping = {};
        
        data
          .sort((a, b) => (a.fieldEntryOrder || 0) - (b.fieldEntryOrder || 0))
          .forEach((item) => {
            const fieldKey = getFieldKeyFromPhysicalName(item.fieldPhysicalName);
            if (fieldKey) {
              const validationConfig = getFieldValidationConfig(
                item.fieldPhysicalName,
                item.fieldDisplayName,
                t
              );

              fieldMapping[fieldKey] = {
                label: item.fieldDisplayName,
                isShow: item.fieldStyleDisplay,
                isRequired: item.fieldStyleRequired,
                id: fieldKey,
                entryOrder: item.fieldEntryOrder || 0,
                fieldDescription: item.fieldDescription || "",
                ...validationConfig,
              };
            }
          });

        setFieldName(fieldMapping);

      } catch (err) {
        console.log(err);
      }
    };

    loadData();
    document.getElementsByTagName("html")[0].classList.add("popup-Page");

    return () => {
      document.getElementsByTagName("html")[0].classList.remove("popup-Page");
    };
  }, [selectedSelected?.studentUID, t]);

  useEffect(() => {
    const fieldKeys = Object.keys(fieldName);
    if (fieldKeys.length > 0) {
      let fieldRequired = Object.values(fieldName).filter(
        (v) => v && v.isRequired && v.isShow
      );
      setSchema(yup.object().shape(fieldRequired.reduce(createYupSchema, {})));
    }
  }, [fieldName]);

  const validateMoneyField = (value, fieldName) => {
    if (value === '' || value === null || value === undefined) {
      formik.setFieldError(fieldName, `${fieldName} is required`);
      return false;
    }
    const numValue = parseNumber(value);
    if (isNaN(numValue) || numValue < 0) {
      formik.setFieldError(fieldName, `${fieldName} must be 0 or greater`);
      return false;
    }
    return true;
  };

  const formik = useFormik({
    initialValues: formValues || initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      // Manually validate money fields
      const moneyFields = [
        { field: 'userDefinedMoney1', required: fieldName['userDefinedMoney1']?.isRequired },
        { field: 'userDefinedMoney2', required: fieldName['userDefinedMoney2']?.isRequired },
        { field: 'userDefinedMoney3', required: fieldName['userDefinedMoney3']?.isRequired }
      ];

      let hasErrors = false;
      moneyFields.forEach(({ field, required }) => {
        if (required && (values[field] === '' || values[field] === null || values[field] === undefined)) {
          formik.setFieldError(field, `${fieldName[field]?.label || field} is required`);
          hasErrors = true;
        }
      });

      if (hasErrors) {
        return;
      }

      try {
        const payloadRequest = {
          systemUID: selectedSelected.studentUID,
          camsModuleID: 6,
          brandonTest1: values.brandonTest1,
          brandonTest2: values.brandonTest2,
          brandonTest3: values.brandonTest3,
          brandonTest: values.brandonTest,
          tianaTest1: values.tianaTest1,
          userDefFreeFormat3: values.userDefFreeFormat3,
          finAidDate: values.finAidDate,
          userDefDate2: values.userDefDate2,
          userDefDate3: values.userDefDate3,
          userDefReal1: values.userDefReal1 === '' ? null : parseFloat(values.userDefReal1),
          userDefReal2: values.userDefReal2 === '' ? null : parseFloat(values.userDefReal2),
          userDefReal3: values.userDefReal3 === '' ? null : parseFloat(values.userDefReal3),
          userDefMoney1: values.userDefinedMoney1 !== '' ? formNumber(values.userDefinedMoney1) : null,
          userDefMoney2: values.userDefinedMoney2 !== '' ? formNumber(values.userDefinedMoney2) : null,
          userDefMoney3: values.userDefinedMoney3 !== '' ? formNumber(values.userDefinedMoney3) : null,
          thirdPartyPackage: values.thirdPartyPackage === '' ? false : values.thirdPartyPackage,
          userDefBit2: values.userDefBit2 === '' ? false : values.userDefBit2,
          userDefBit3: values.userDefBit3 === '' ? false : values.userDefBit3,
          userDefNote1: values.userDefNote1,
          userDefNote2: values.userDefNote2,
          userDefNote3: values.userDefNote3
        };

        await putApi(`${userdefined_update}`, payloadRequest);
        
        toastMessage(
          t('popupMessages.successIcon'), 
          t('popupMessages.success'), 
          t('popupMessages.successfullyUpdated')
        );
        
        onClose('');
      } catch (err) {
        console.error('Update error:', err);
        toastMessage(
          t('popupMessages.errorIcon'),
          t('popupMessages.error'),
          t('popupMessages.errorUpdating')
        );
      }
    }
  });

  useEffect(() => {
    if (!formik.isSubmitting) return;
    if (Object.keys(formik.errors).length > 0) {
      scrollIntoViewHelper(formik.errors);
    }
  }, [formik]);

  const { access } = useSelector(updateAccessSelector);
  const { nestedParentLevel } = access;
  const [statusAccess, setStatusAccess] = useState({ maintenance: 2 });
  const [accessFlag, setAccessFlag] = useState(false);

  useEffect(() => {
    let finStatusAccess = nestedParentLevel.filter((v, i) =>
      v.menuItemName.trim() === maintenance && v.moduleorder === 4
    );

    setStatusAccess({
      ...statusAccess,
      maintenance: (finStatusAccess.length > 0) ? finStatusAccess[0].menuAccess : 2,
    });
  }, [access]);

  useEffect(() => {
    (statusAccess.maintenance !== 2) ? setAccessFlag(true) : setAccessFlag(false);
  }, [statusAccess]);

  const renderFieldComponent = (fieldKey, fieldConfig) => {
    const key = `field-${fieldKey}`;

    switch (fieldConfig.field) {
      case "select":
        return (
          <FormGroup key={key}>
            <SelectInput
              disabled={accessFlag}
              label={fieldConfig.label}
              labelId={fieldKey}
              title={fieldConfig.fieldDescription}
              formikValue={formik.values[fieldKey]}
              formikChange={formik.handleChange}
              isDoubled={true}
              mandatory={fieldConfig.isRequired}
              errors={fieldConfig.isRequired ? formik.errors[fieldKey] : ""}
              touched={fieldConfig.isRequired ? formik.touched[fieldKey] : ""}
              dropdownData={{
                key: "displayText",
                value: "uniqueId",
                optionLists: Dropdown[fieldConfig.dropdownKey] || [],
              }}
            />
          </FormGroup>
        );

      case "text":
        const isNumeric = fieldConfig.isNumeric || fieldConfig.validationType === "number";
        const isCurrency = fieldConfig.isCurrency || fieldConfig.validationType === "currency";

        return (
          <FormGroup key={key}>
            <TextInput
              disabled={accessFlag}
              label={fieldConfig.label}
              labelId={fieldKey}
              title={fieldConfig.fieldDescription}
              onlyNumeric={isNumeric}
              onBlur={isCurrency ? (e => { onSwipeCurrency(e); formik.handleBlur(e); }) : formik.handleBlur}
              formikValue={formik.values[fieldKey]}
              formikChange={formik.handleChange}
              isDoubled={true}
              mandatory={fieldConfig.isRequired}
              errors={fieldConfig.isRequired ? formik.errors[fieldKey] : ""}
              touched={fieldConfig.isRequired ? formik.touched[fieldKey] : ""}
            />
          </FormGroup>
        );

      case "date":
        return (
          <FormGroup key={key}>
            <DateInput
              disabled={accessFlag}
              labelId={fieldKey}
              label={fieldConfig.label}
              title={fieldConfig.fieldDescription}
              formik={formik}
              isDoubled={true}
              defaultValue={formik.values[fieldKey]}
              mandatory={fieldConfig.isRequired}
              errors={fieldConfig.isRequired ? formik.errors[fieldKey] : ""}
              touched={fieldConfig.isRequired ? formik.touched[fieldKey] : ""}
            />
          </FormGroup>
        );

      case "checkbox":
        return (
          <FormGroup key={key}>
            <CheckBoxInput
              disabled={accessFlag}
              label={fieldConfig.label}
              labelId={fieldKey}
              title={fieldConfig.fieldDescription}
              formikValue={formik.values[fieldKey]}
              formikChange={formik.handleChange}
              checked={formik.values[fieldKey]}
              isDoubled={true}
              mandatory={fieldConfig.isRequired}
              errors={fieldConfig.isRequired ? formik.errors[fieldKey] : ""}
              touched={fieldConfig.isRequired ? formik.touched[fieldKey] : ""}
            />
          </FormGroup>
        );

      case "textarea":
        return (
          <FormGroup key={key}>
            <TextAreaInput
              disabled={accessFlag}
              label={fieldConfig.label}
              labelId={fieldKey}
              title={fieldConfig.fieldDescription}
              formikChange={formik.handleChange}
              formikValue={formik.values[fieldKey]}
              onBlur={formik.handleBlur}
              isDoubled={true}
              mandatory={fieldConfig.isRequired}
              errors={fieldConfig.isRequired ? formik.errors[fieldKey] : ""}
              touched={fieldConfig.isRequired ? formik.touched[fieldKey] : ""}
            />
          </FormGroup>
        );

      default:
        return null;
    }
  };

  const renderFieldsInOrder = () => {
    const orderedFields = Object.entries(fieldName)
      .filter(([key, field]) => field && field.isShow)
      .sort((a, b) => (a[1].entryOrder || 99) - (b[1].entryOrder || 99));

    return orderedFields.map(([fieldKey, fieldConfig]) => {
      return renderFieldComponent(fieldKey, fieldConfig);
    });
  };

  return (
    <>
      <PopupHeader
        title={t("commonLabel.updateUserDefinedFields", { ns: "financialAid" })}
        navigationList={financialAid_userDefinedFields}
        onClose={onClose}
      />
      <form className={classess.root} onSubmit={formik.handleSubmit}>
        <CardContent>
          {renderFieldsInOrder()}
          <div style={{ display: "flex" }}>
            <ButtonWrapperFilter>
              <Button
                className="BgYellowOrange Search"
                variant="contained"
                size="large"
                type="submit"
                disabled={accessFlag}
              >
                {t('buttonLabel.update')}
              </Button>
              <Button
                aria-label={"Cancel"}
                className="BgLightBlue"
                variant="contained"
                size="large"
                onClick={() => onClose("")}
              >
                {t('buttonLabel.cancel')}
              </Button>
            </ButtonWrapperFilter>
          </div>
        </CardContent>
      </form>
    </>
  );
};

export default AddUserdefinedfields;